import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Info, Loader2 } from "lucide-react";
import {
  errorMessage,
  moveContacts,
  type ActionResult,
  type Campaign,
  type CampaignContact,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScopeBadge } from "@/components/email/EmailBadges";
import { plural } from "@/lib/email";
import { cn } from "@/lib/utils";

/** Move contacts to another campaign of their project — explicitly, with
 *  what happens spelled out: the old membership ends (kept for history),
 *  a new one starts from scratch. */
export function ChangeCampaignDialog({
  slug,
  rows,
  campaigns,
  onOpenChange,
  onMoved,
}: {
  slug: string;
  rows: CampaignContact[] | null;
  campaigns: Campaign[];
  onOpenChange: (open: boolean) => void;
  onMoved: (result: ActionResult) => void;
}) {
  const [target, setTarget] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const open = !!rows?.length;

  useEffect(() => {
    if (open) setTarget(null);
  }, [open]);

  const projectIds = useMemo(() => new Set((rows ?? []).map((r) => r.project_id)), [rows]);
  const sources = useMemo(() => new Set((rows ?? []).map((r) => r.campaign_id)), [rows]);
  const choices = campaigns.filter((c) => projectIds.has(c.project_id) && !(sources.size === 1 && sources.has(c.id)));
  const chosen = choices.find((c) => c.id === target) ?? null;
  const moving = (rows ?? []).filter((r) => chosen && r.project_id === chosen.project_id && r.campaign_id !== chosen.id);
  const sourceNames = [...new Set((rows ?? []).map((r) => r.campaign_name))];

  async function submit() {
    if (!chosen || !moving.length) return;
    setSaving(true);
    try {
      onMoved(await moveContacts(slug, moving.map((r) => r.id), chosen.id));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-5">
        <DialogHeader>
          <DialogTitle>Change campaign</DialogTitle>
          <DialogDescription>
            {rows?.length === 1
              ? `${rows[0].contact_name} leaves “${rows[0].campaign_name}”.`
              : `${rows?.length ?? 0} contacts leave ${sourceNames.length === 1 ? `“${sourceNames[0]}”` : "their campaigns"}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <p className="type-zonehead">Destination</p>
          {choices.length ? (
            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1" role="radiogroup" aria-label="Destination campaign">
              {choices.map((c) => {
                const active = c.id === target;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setTarget(c.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-all",
                      active ? "border-indigo-300 bg-indigo-50/60 ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300",
                    )}
                  >
                    <ScopeBadge scope={c.scope} />
                    <span className="flex-1 truncate font-medium text-slate-800">{c.name}</span>
                    <span className="text-xs text-slate-400">
                      {plural(c.steps_count, "step")} · {plural(c.contacts_count, "contact")}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
              No other open campaign in this project.
            </p>
          )}
        </div>

        <div className="flex gap-3 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <div className="space-y-1">
            <p>
              {(rows ?? []).some((r) => r.provider_lead_id && !r.dry_run)
                ? "Lemlist stops their sequence in the old campaign; "
                : "Nothing changes in Lemlist for the old campaign; "}
              that membership is kept, marked
              <span className="font-medium"> Moved</span>, with everything already sent.
            </p>
            <p>
              In the new campaign they start from the first step as <span className="font-medium">Ready</span>:
              no progress is carried over, and nothing is sent before the next sync.
            </p>
            {chosen && moving.length < (rows?.length ?? 0) && (
              <p className="font-medium text-amber-700">
                {(rows?.length ?? 0) - moving.length} selected contact(s) belong to another project
                or already are in this campaign: they stay where they are.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!chosen || !moving.length || saving} onClick={() => void submit()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {chosen ? `Move ${moving.length} to “${chosen.name}”` : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
